import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { Users, KeyRound, Copy, Trash2, ShieldCheck } from "lucide-react";
import { toast } from "sonner";

function InvitePanel({ invites, onChanged }) {
  const [role, setRole] = useState("employee");
  const [note, setNote] = useState("");
  const create = async () => {
    try { const { data } = await api.post("/team/invites", { role, note }); toast.success(`Invite code ${data.code} created (valid 7 days)`); setNote(""); onChanged(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Could not create invite"); }
  };
  const revoke = async (code) => { await api.delete(`/team/invites/${code}`); toast.success("Invite revoked"); onChanged(); };
  const copy = (code) => { navigator.clipboard?.writeText(code); toast.success("Code copied"); };
  return (
    <Card data-testid="invite-panel" className="p-5 bg-white border-slate-200">
      <div className="font-display font-bold text-[#0A2A5E] flex items-center gap-2"><KeyRound className="w-4 h-4 text-[#7C3F00]" /> Invite codes</div>
      <p className="text-xs text-slate-500 mt-1">New teammates enter a code when they sign up (email or Google) to join <strong>your</strong> organization with the role you choose. Codes are single-use and expire in 7 days. Without a code, a signup creates a separate, empty workspace.</p>
      <div className="flex flex-wrap gap-2 mt-3">
        <Select value={role} onValueChange={setRole}><SelectTrigger data-testid="invite-role" className="h-9 w-40"><SelectValue /></SelectTrigger>
          <SelectContent><SelectItem value="employee">Employee</SelectItem><SelectItem value="manager">HR Manager</SelectItem></SelectContent></Select>
        <Input data-testid="invite-note" value={note} onChange={(e) => setNote(e.target.value)} placeholder="Note (e.g. name of invitee)" className="h-9 flex-1 min-w-[10rem]" />
        <Button data-testid="invite-create" onClick={create} className="h-9 bg-[#0A2A5E] text-white">Generate code</Button>
      </div>
      <ul className="mt-3 divide-y divide-slate-100">
        {invites.map((i) => (
          <li key={i.code} data-testid={`invite-${i.code}`} className="py-2 flex items-center gap-3 text-xs">
            <code className="font-mono font-bold text-sm text-[#0A2A5E] tracking-wider">{i.code}</code>
            <span className="px-1.5 py-0.5 rounded bg-[#FDECD0] text-[#7C3F00] font-semibold uppercase text-[10px]">{i.role === "manager" ? "HR Manager" : "Employee"}</span>
            <span className="text-slate-500 flex-1 truncate">{i.note} · expires {i.expires_at.slice(0, 10)}</span>
            <button type="button" onClick={() => copy(i.code)} className="p-1 rounded hover:bg-slate-100" aria-label="Copy"><Copy className="w-3.5 h-3.5" /></button>
            <button type="button" data-testid={`invite-revoke-${i.code}`} onClick={() => revoke(i.code)} className="p-1 rounded hover:bg-rose-50 text-rose-700" aria-label="Revoke"><Trash2 className="w-3.5 h-3.5" /></button>
          </li>
        ))}
        {invites.length === 0 && <li className="py-2 text-xs text-slate-500">No active invite codes.</li>}
      </ul>
    </Card>
  );
}

export default function Team() {
  const { user } = useAuth();
  const [data, setData] = useState(null);
  const load = () => api.get("/team").then((r) => setData(r.data)).catch(() => toast.error("Could not load team"));
  useEffect(() => { load(); }, []);
  const setRole = async (u, role) => {
    if (!window.confirm(`Change ${u.name}'s role to ${role === "manager" ? "HR Manager" : "Employee"}?`)) return;
    try { await api.patch(`/team/${u.id}/role`, { role }); toast.success("Role updated"); load(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Could not change role"); }
  };
  if (user?.role !== "manager") return <div className="p-8 text-center text-slate-600" data-testid="team-denied">HR Manager access required.</div>;
  if (!data) return null;
  return (
    <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-6 space-y-5">
      <div>
        <div className="text-xs uppercase tracking-[0.2em] text-[#C7761A] font-bold">Organization</div>
        <h1 className="font-display text-2xl sm:text-3xl font-bold text-[#0A2A5E] flex items-center gap-2"><Users className="w-7 h-7" /> Team & access</h1>
        <p className="text-sm text-slate-600 mt-1">Roles are assigned here by HR Managers — never self-selected at signup. Every change is recorded in the audit trail.</p>
      </div>
      <InvitePanel invites={data.invites} onChanged={load} />
      <Card data-testid="team-list" className="p-5 bg-white border-slate-200">
        <div className="font-display font-bold text-[#0A2A5E] flex items-center gap-2"><ShieldCheck className="w-4 h-4 text-[#7C3F00]" /> Members ({data.users.length})</div>
        <ul className="mt-2 divide-y divide-slate-100">
          {data.users.map((u) => (
            <li key={u.id} data-testid={`member-${u.id}`} className="py-2.5 flex flex-wrap items-center gap-3 text-sm">
              <div className="min-w-0 flex-1"><div className="font-semibold text-slate-800 truncate">{u.name}{u.id === user.id && <span className="text-slate-400 font-normal"> (you)</span>}</div><div className="text-xs text-slate-500 truncate">{u.email}{u.reviewer_roles?.length ? ` · reviewer: ${u.reviewer_roles.join(", ")}` : ""}</div></div>
              <Select value={u.role} onValueChange={(v) => setRole(u, v)} disabled={u.id === user.id}>
                <SelectTrigger data-testid={`member-role-${u.id}`} className="h-8 w-36 text-xs"><SelectValue /></SelectTrigger>
                <SelectContent><SelectItem value="employee">Employee</SelectItem><SelectItem value="manager">HR Manager</SelectItem></SelectContent>
              </Select>
            </li>
          ))}
        </ul>
      </Card>
    </div>
  );
}
